import React, { useState, useEffect, useCallback, Suspense } from 'react';
import { Navigation, Pagination, Keyboard, A11y } from 'swiper/modules';
// eslint-disable-next-line import/no-unresolved
import { Swiper, SwiperSlide } from 'swiper/react';
import { useBreakpoint } from 'gatsby-plugin-breakpoints';
import { bpProductGallery } from '../../Carousel/breakpoints';
import { slugify } from '../../../utils/slugify';
import {
  addCarouselBulletAnalyticEvents,
  carouselAnalyticsEvents,
} from '../../Carousel/utils/carouselAnalyticsEvents';
import { ImageLoadingType } from '../../UI/Image/Image';
import {
  ImageGallery,
  ImagesContainer,
  PrimaryImageContainer,
  PrimaryImage,
  ImageWrap,
  SecondaryImages,
  CarouselWrapper,
  CarouselImage,
  DesktopWrapper,
} from './styles';

interface Props {
  imageSet: Record<string, any>;
  isPhoneActive: boolean;
}

const Gallery = ({ imageSet, isPhoneActive }: Props) => {
  // {isPhoneActive} - boolean - if true it will use all 3 images that come from variants. If flase will use only two images that come from phone model.
  // {imageSet} - object - contains 2-3 phone images with alt text.
  const breakpoints = useBreakpoint();
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);
  // Get sectionId and slugify for classNames
  const sectionId = imageSet.phoneFrontImage.altText;
  const sectionTarget = slugify(sectionId || 'Phone Gallery Carousel');

  const addAnalyticEvents = useCallback(
    () => addCarouselBulletAnalyticEvents(sectionTarget, sectionId),
    [sectionId, sectionTarget],
  );
  
  useEffect(addAnalyticEvents, [addAnalyticEvents]);

  // Set large main image to state
  const [primaryImage, setPrimaryImage] = useState(imageSet.phoneFrontImage);

  // When a new image set is delivered, switch primary image to match
  useEffect(() => {
    setPrimaryImage(imageSet.phoneFrontImage);
  }, [imageSet]);
  return (
    <ImageGallery>
      <DesktopWrapper>
        {/* LARGE IMAGE ON DISPLAY */}
        <PrimaryImageContainer>
          <PrimaryImage
            image={primaryImage}
            objectFit="contain"
            loading={ImageLoadingType.Eager}
            data-testid="primary-image-desktop"
          />
        </PrimaryImageContainer>
        {/* DESKTOP IMAGES */}
        <ImagesContainer>
          {/* FRONT IMAGE */}
          <ImageWrap
            onClick={() => setPrimaryImage(imageSet.phoneFrontImage)}
            selectedImg={primaryImage.id}
            image={imageSet.phoneFrontImage.id}
          >
            <SecondaryImages
              image={imageSet.phoneFrontImage}
              objectFit="contain"
              loading={ImageLoadingType.Lazy}
            />
          </ImageWrap>

          {/* BACK IMAGE */}
          <ImageWrap
            onClick={() => setPrimaryImage(imageSet.phoneBackImage)}
            selectedImg={primaryImage.id}
            image={imageSet.phoneBackImage.id}
          >
            <SecondaryImages
              image={imageSet.phoneBackImage}
              objectFit="contain"
              loading={ImageLoadingType.Lazy}
            />
          </ImageWrap>

          {/* SIDE IMAGE */}
          {isPhoneActive && imageSet?.phoneSideImage && (
            <ImageWrap
              onClick={() => setPrimaryImage(imageSet.phoneSideImage)}
              selectedImg={primaryImage.id}
              image={imageSet.phoneSideImage.id}
            >
              <SecondaryImages
                image={imageSet.phoneSideImage}
                objectFit="contain"
                loading={ImageLoadingType.Lazy}
              />
            </ImageWrap>
          )}
        </ImagesContainer>
      </DesktopWrapper>
      {/* MOBILE CAROUSEL */}
      {!breakpoints.desktop && isClient && (
        <CarouselWrapper>
          <Suspense>
            <Swiper
              tabIndex={0}
              speed={1000}
              breakpoints={bpProductGallery}
              navigation
              pagination={{
                clickable: true,
              }}
              a11y={{
                slideRole: 'group',
                enabled: true,
              }}
              allowTouchMove
              keyboard
              modules={[Navigation, Pagination, Keyboard, A11y]}
              className={`swiper ${sectionTarget}`}
              {...carouselAnalyticsEvents(sectionId)}
            >
              {/* FRONT IMAGE */}
              <SwiperSlide key={`image-${imageSet.phoneFrontImage?.asset.url}`}>
                <CarouselImage image={imageSet.phoneFrontImage} loading={ImageLoadingType.Eager} />
              </SwiperSlide>
              {/* BACK IMAGE */}
              {isPhoneActive && imageSet?.phoneBackImage && (
                <SwiperSlide key={`image-${imageSet.phoneBackImage?.asset.url}`}>
                  <CarouselImage image={imageSet.phoneBackImage} loading={ImageLoadingType.Lazy} />
                </SwiperSlide>
              )}
              {/* SIDE IMAGE */}
              {isPhoneActive && imageSet?.phoneSideImage && (
                <SwiperSlide key={`image-${imageSet.phoneSideImage?.asset.url}`}>
                  <CarouselImage image={imageSet.phoneSideImage} loading={ImageLoadingType.Lazy} />
                </SwiperSlide>
              )}
            </Swiper>
          </Suspense>
        </CarouselWrapper>
      )}
    </ImageGallery>
  );
};

export default Gallery;
